import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
    CpmButton,
    CpmCheckbox,
    CpmDateRangeFilter,
    CpmEmptyState,
    CpmFilterDropdown,
    CpmInlineErrorBanner,
    CpmPageShell,
    CpmPagination,
    CpmPaginationLabel,
    CpmQueueListLoading,
    CpmSelectionBar,
    CpmStoreBrandFilter,
    CpmTypeChip,
} from '../../../components/design-system';
import {
    CPM_BAND_OPTIONS,
    CPM_BARCODE_SORT_OPTIONS,
    CPM_LINK_SORT_OPTIONS,
    CPM_METHOD_OPTIONS,
    CPM_PENDING_SORT_OPTIONS,
    CPM_QUEUE_RESOLUTION,
    CPM_RECORD_TYPE,
    CPM_RECORD_TYPE_OPTIONS,
    CPM_SIGNAL_OPTIONS,
    CPM_TAB_TO_QUEUE_TYPE,
    formatCpmCount,
    formatCreatedVia,
    formatFlaggedDate,
    duplicateTargetName,
    packLabel,
    sourceTypeToRecordType,
} from '../../../constants/centralProductMaster.constants';
import { useApiClient } from '../../../hooks/useApiClient';
import {
    bulkApproveQueue,
    bulkResolveQueue,
    resolveProductLink,
} from '../../../services/productMasterService';
import { useAppDispatch } from '../../../store';
import {
    beginListReload,
    clearFilters,
    clearSelection,
    fetchQueueCounts,
    fetchVerificationQueue,
    patchFilters,
    setActiveTab,
    setPage,
    setPageSelection,
    setPendingView,
    toggleSelectedItem,
    useCpmVerificationQueue,
    type QueueSelectedItem,
} from '../../../store/slices/cpmVerificationQueueSlice';
import type {
    CpmBarcodeConflictItem,
    CpmBulkResult,
    CpmLinkReviewItem,
    CpmPendingItem,
    CpmQueueTab,
} from '../../../types/centralProductMaster.types';
import { toast } from '../../../utils/toast';
import MergeDecision from '../MergeDecision/MergeDecision';
import ReviewDetail from '../ReviewDetail/ReviewDetail';
import BarcodeConflictTable from './components/BarcodeConflictTable';
import BulkActionModal from './components/BulkActionModal';
import { buildPendingChains } from './components/buildPendingChains';
import LinkReviewTable from './components/LinkReviewTable';
import PendingGroupedView from './components/PendingGroupedView';
import QuickReviewModal from './components/QuickReviewModal';
import RejectModal from './components/RejectModal';

function isPendingItem(item: QueueSelectedItem): item is CpmPendingItem {
    return 'recordType' in item && 'level' in item;
}

function isLinkItem(item: QueueSelectedItem): item is CpmLinkReviewItem {
    return 'sourceType' in item;
}

type ReviewTarget =
    | { mode: 'pending'; item: CpmPendingItem }
    | { mode: 'link'; item: CpmLinkReviewItem }
    | { mode: 'barcode'; item: CpmBarcodeConflictItem };

const IconCheck = ({ size = 14 }: { size?: number }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
    >
        <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconX = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
    >
        <circle cx="12" cy="12" r="10" />
        <path d="m15 9-6 6M9 9l6 6" strokeLinecap="round" />
    </svg>
);

const IconMerge = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
    >
        <path d="m8 6 4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
        <path
            d="M12 2v10.3a4 4 0 0 1-1.172 2.83L4 22"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="m20 22-5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconSort = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--cpm-text-tertiary)"
        strokeWidth="2"
    >
        <path d="M4 6h10M4 12h7M4 18h4" strokeLinecap="round" />
        <path d="m17 8 3-3 3 3M20 5v14" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

function sortToken(sortBy: string, sortDir: string, tab: CpmQueueTab): string {
    const defaults: Record<CpmQueueTab, string> = {
        pending: 'createdAt:desc',
        link: 'confidence:desc',
        barcode: 'firstConflictAt:desc',
    };
    const token = `${sortBy}:${sortDir}`;
    return token === defaults[tab] ? '' : token;
}

function applySortToken(
    token: string,
    tab: CpmQueueTab
): { sortBy: string; sortDir: 'asc' | 'desc' } {
    if (!token) {
        if (tab === 'link') return { sortBy: 'confidence', sortDir: 'desc' };
        if (tab === 'barcode') return { sortBy: 'firstConflictAt', sortDir: 'desc' };
        return { sortBy: 'createdAt', sortDir: 'desc' };
    }
    const [sortBy, sortDir] = token.split(':');
    return { sortBy, sortDir: sortDir === 'asc' ? 'asc' : 'desc' };
}

function masterEditorHrefForSurvivor(
    level: 'brand' | 'product' | 'variant',
    survivorId: string
): string {
    const p = new URLSearchParams();
    if (level === 'brand') {
        p.set('level', 'brands');
    } else if (level === 'product') {
        p.set('level', 'catalog');
        p.set('view', 'products');
    } else {
        p.set('level', 'catalog');
        p.set('view', 'variants');
    }
    p.set('openId', survivorId);
    p.set('openLevel', level);
    return `/central-product-master/master-editor?${p.toString()}`;
}

export default function VerificationQueue() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const queue = useCpmVerificationQueue();
    const { callApi, loading: mutating } = useApiClient({ toast: true });
    const [searchParams, setSearchParams] = useSearchParams();

    const [rejectTarget, setRejectTarget] = useState<CpmPendingItem | null>(null);
    const [quickTarget, setQuickTarget] = useState<CpmPendingItem | null>(null);
    const [bulkModal, setBulkModal] = useState<{
        kind: 'approve' | 'resolve';
        path: 'picked' | 'filtered';
    } | null>(null);
    const [promotedIds, setPromotedIds] = useState<Set<string>>(() => new Set());
    const [mergeState, setMergeState] = useState<{
        level: 'brand' | 'product' | 'variant';
        idA: string;
        idB: string;
    } | null>(null);
    const [reviewTarget, setReviewTarget] = useState<ReviewTarget | null>(null);

    const activeTab = queue.activeTab;
    const filters = queue.filtersByTab[activeTab];
    const bucket = queue.lists[activeTab];
    const storeBrandQueueType = CPM_TAB_TO_QUEUE_TYPE[activeTab];
    const storeBrandFilterContext = useMemo(
        () => ({
            band: activeTab === 'link' ? filters.band : [],
            method: activeTab === 'barcode' ? [] : filters.method,
            recordType: activeTab === 'barcode' ? [] : filters.recordType,
            signals: activeTab === 'pending' ? filters.signals : [],
            disputed: activeTab === 'barcode' ? filters.disputed : null,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
        }),
        [
            activeTab,
            filters.band,
            filters.method,
            filters.recordType,
            filters.signals,
            filters.disputed,
            filters.dateFrom,
            filters.dateTo,
        ]
    );

    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam === 'pending' || tabParam === 'link' || tabParam === 'barcode') {
            if (tabParam !== activeTab) dispatch(setActiveTab(tabParam));
        }
        if (searchParams.has('modal') || searchParams.has('mode') || searchParams.has('id')) {
            const next = new URLSearchParams(searchParams);
            next.delete('modal');
            next.delete('mode');
            next.delete('id');
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(fetchVerificationQueue(activeTab));
    }, [dispatch, activeTab, filters, queue.pendingView]);

    useEffect(() => {
        dispatch(fetchQueueCounts());
    }, [dispatch]);

    const selectTab = (tab: CpmQueueTab) => {
        dispatch(setActiveTab(tab));
        setReviewTarget(null);
        const next = new URLSearchParams(searchParams);
        next.set('tab', tab);
        next.delete('modal');
        next.delete('mode');
        next.delete('id');
        setSearchParams(next, { replace: true });
    };

    const pendingItems = bucket.items as CpmPendingItem[];
    const linkItems = bucket.items as CpmLinkReviewItem[];
    const barcodeItems = bucket.items as CpmBarcodeConflictItem[];

    const { groupChains, standalone } = useMemo(
        () =>
            activeTab === 'pending'
                ? buildPendingChains(pendingItems)
                : { groupChains: [], standalone: [] },
        [activeTab, pendingItems]
    );

    const selectedPending = queue.selectedIds
        .map((id) => queue.selectedItemsById[id])
        .filter((item): item is CpmPendingItem => !!item && isPendingItem(item));
    const selectedLinks = queue.selectedIds
        .map((id) => queue.selectedItemsById[id])
        .filter((item): item is CpmLinkReviewItem => !!item && isLinkItem(item));

    const canReject =
        activeTab === 'pending' &&
        selectedPending.length === 1 &&
        selectedPending[0]?.level !== undefined;
    const canMerge =
        activeTab === 'pending' &&
        selectedPending.length === 2 &&
        selectedPending[0]?.level === selectedPending[1]?.level;
    const canBulkApprove = activeTab === 'pending' && selectedPending.length > 0;
    const canBulkResolve = activeTab === 'link' && selectedLinks.length > 0;

    const activeTabTotal = queue.activeTabTotal;
    const matchingTotal = bucket.recordTotal ?? bucket.pagination?.total ?? activeTabTotal ?? 0;

    const paginationNoun =
        activeTab === 'pending'
            ? queue.pendingView === 'grouped'
                ? 'chains'
                : 'records'
            : activeTab === 'barcode'
              ? 'barcodes'
              : 'links';

    const openReview = (target: ReviewTarget) => {
        setReviewTarget(target);
    };

    const openPendingById = (id: string) => {
        const item = pendingItems.find((i) => i._id === id);
        if (item) {
            setReviewTarget({ mode: 'pending', item });
        }
    };

    /** Design: clean pending rows → Quick Review; blocked/dup → Full Detail. */
    const openPendingRow = (row: CpmPendingItem) => {
        const blocked = !row.parentIsActive && !!row.parent;
        const dup = !!row.possibleDuplicateOf;
        if (blocked || dup) {
            openReview({ mode: 'pending', item: row });
            return;
        }
        setQuickTarget(row);
    };

    const closeReview = () => {
        setReviewTarget(null);
    };

    const tableReplaceLoading =
        bucket.loading === 'loading' &&
        (queue.listIntent === 'replace' || bucket.items.length === 0);
    const tableBusyPage =
        bucket.loading === 'loading' && queue.listIntent === 'page' && bucket.items.length > 0;

    const refresh = () => {
        dispatch(beginListReload('Reloading the queue'));
        dispatch(fetchVerificationQueue(activeTab));
        dispatch(fetchQueueCounts());
        dispatch(clearSelection());
        setPromotedIds(new Set());
    };

    const runBulkConfirm = async (): Promise<CpmBulkResult | null> => {
        if (!bulkModal) return null;
        if (bulkModal.kind === 'approve' && bulkModal.path === 'picked') {
            const items = selectedPending.map((row) => ({
                id: row._id,
                level: row.level,
            }));
            const result = await callApi(() => bulkApproveQueue({ items }));
            if (result) refresh();
            return result;
        }
        if (bulkModal.kind === 'approve' && bulkModal.path === 'filtered') {
            const filter: Record<string, unknown> = {};
            if (filters.signals.length) filter.signals = filters.signals;
            if (filters.method.length) filter.method = filters.method;
            if (filters.recordType.length) filter.recordType = filters.recordType;
            if (filters.storeBrand) filter.storeBrand = filters.storeBrand;
            if (filters.dateFrom) filter.dateFrom = filters.dateFrom;
            if (filters.dateTo) filter.dateTo = filters.dateTo;
            const result = await callApi(() => bulkApproveQueue({ filter }));
            if (result) refresh();
            return result;
        }
        if (bulkModal.kind === 'resolve' && bulkModal.path === 'filtered') {
            const filter: Record<string, unknown> = {};
            if (filters.band.length) filter.band = filters.band;
            if (filters.method.length) filter.method = filters.method;
            if (filters.recordType.length) filter.recordType = filters.recordType;
            if (filters.storeBrand) filter.storeBrand = filters.storeBrand;
            if (filters.dateFrom) filter.dateFrom = filters.dateFrom;
            if (filters.dateTo) filter.dateTo = filters.dateTo;
            const result = await callApi(() => bulkResolveQueue({ filter }));
            if (result) refresh();
            return result;
        }
        if (bulkModal.kind === 'resolve') {
            const result = await callApi(() =>
                bulkResolveQueue({
                    items: selectedLinks.map((row) => ({ productLinkId: row._id })),
                })
            );
            if (result) refresh();
            return result;
        }
        return null;
    };

    const handleApproveChain = async (nodes: CpmPendingItem[]): Promise<boolean> => {
        const items = nodes.map((n) => ({ id: n._id, level: n.level }));
        const result = await callApi(() => bulkApproveQueue({ items }));
        if (!result) return false;
        setPromotedIds((prev) => {
            const next = new Set(prev);
            result.succeeded.forEach((id) => next.add(id));
            return next;
        });
        refresh();
        return true;
    };

    const handleAcceptLink = async (
        row: CpmLinkReviewItem,
        selectedMasterId?: string
    ): Promise<boolean> => {
        const isVariant = sourceTypeToRecordType(row.sourceType) === CPM_RECORD_TYPE.VARIANT;
        const inheritOk =
            isVariant !== true ||
            (selectedMasterId == null && row.hasLiveStateProductForTopCandidate === true);
        if (inheritOk !== true) {
            openReview({ mode: 'link', item: row });
            return false;
        }
        const ok = await callApi(() =>
            resolveProductLink({
                productLinkId: row._id,
                resolution: selectedMasterId
                    ? CPM_QUEUE_RESOLUTION.PICK_DIFFERENT
                    : CPM_QUEUE_RESOLUTION.ACCEPT_AI,
                selectedMasterId,
            })
        );
        if (ok == null) return false;
        toast.success(selectedMasterId ? 'Linked to selected candidate' : 'Accepted AI suggestion');
        // Keep row visible with Linked/Undo chrome (design); refresh totals only.
        dispatch(fetchQueueCounts());
        return true;
    };

    const reviewPending = reviewTarget?.mode === 'pending' ? reviewTarget.item : null;
    const reviewLink = reviewTarget?.mode === 'link' ? reviewTarget.item : null;
    const reviewBarcode = reviewTarget?.mode === 'barcode' ? reviewTarget.item : null;

    const sortOptions =
        activeTab === 'barcode'
            ? CPM_BARCODE_SORT_OPTIONS
            : activeTab === 'link'
              ? CPM_LINK_SORT_OPTIONS
              : CPM_PENDING_SORT_OPTIONS;

    const currentSort = sortToken(filters.sortBy, filters.sortDir, activeTab);
    const sortShort =
        sortOptions.find((o) => o.value === currentSort)?.short ??
        sortOptions[0]?.short ??
        'Newest first';

    const activeFilterChips = useMemo(() => {
        const chips: Array<{ key: string; value: string; clear: () => void }> = [];
        if (activeTab === 'link' && filters.band.length) {
            chips.push({
                key: 'Band',
                value: filters.band
                    .map((b) => CPM_BAND_OPTIONS.find((o) => o.value === b)?.label ?? b)
                    .join(', '),
                clear: () => dispatch(patchFilters({ tab: activeTab, patch: { band: [] } })),
            });
        }
        if (activeTab === 'pending' && filters.signals.length) {
            chips.push({
                key: 'Signal',
                value: filters.signals
                    .map((s) => CPM_SIGNAL_OPTIONS.find((o) => o.value === s)?.label ?? s)
                    .join(', '),
                clear: () => dispatch(patchFilters({ tab: activeTab, patch: { signals: [] } })),
            });
        }
        if (activeTab !== 'barcode' && filters.method.length) {
            chips.push({
                key: 'Method',
                value: filters.method
                    .map((m) => CPM_METHOD_OPTIONS.find((o) => o.value === m)?.label ?? m)
                    .join(', '),
                clear: () => dispatch(patchFilters({ tab: activeTab, patch: { method: [] } })),
            });
        }
        if (activeTab !== 'barcode' && filters.recordType.length) {
            chips.push({
                key: 'Type',
                value: filters.recordType
                    .map((t) => CPM_RECORD_TYPE_OPTIONS.find((o) => o.value === t)?.label ?? t)
                    .join(', '),
                clear: () => dispatch(patchFilters({ tab: activeTab, patch: { recordType: [] } })),
            });
        }
        if (filters.storeBrand && filters.storeBrandLabel) {
            chips.push({
                key: 'Store brand',
                value: filters.storeBrandLabel,
                clear: () =>
                    dispatch(
                        patchFilters({
                            tab: activeTab,
                            patch: { storeBrand: null, storeBrandLabel: null },
                        })
                    ),
            });
        }
        if (filters.dateFrom || filters.dateTo) {
            chips.push({
                key: 'Dates',
                value: [formatFlaggedDate(filters.dateFrom), formatFlaggedDate(filters.dateTo)]
                    .filter((x) => x !== '—')
                    .join(' – '),
                clear: () =>
                    dispatch(
                        patchFilters({ tab: activeTab, patch: { dateFrom: null, dateTo: null } })
                    ),
            });
        }
        if (activeTab === 'barcode' && filters.disputed != null) {
            chips.push({
                key: 'Signal',
                value: filters.disputed ? 'Disputed' : 'Conflicted',
                clear: () => dispatch(patchFilters({ tab: activeTab, patch: { disputed: null } })),
            });
        }
        return chips;
    }, [activeTab, filters, dispatch]);

    const dupSelectedCount = selectedPending.filter((r) => !!r.possibleDuplicateOf).length;

    const tabButtonClass = (active: boolean) =>
        [
            'rounded-t-[var(--cpm-radius-10)] cursor-pointer flex items-center border-b-2 -mb-px transition-colors',
            active
                ? 'text-[var(--cpm-brand-navy)] bg-white border-[var(--cpm-brand-purple)]'
                : 'text-[var(--cpm-text-secondary)] border-transparent hover:text-[var(--cpm-brand-navy)]',
        ].join(' ');

    const tabBadgeClass = (active: boolean) =>
        [
            'rounded-full tabular',
            active
                ? 'bg-[var(--cpm-brand-navy)] text-white'
                : 'bg-[var(--cpm-surface-skeleton)] text-[var(--cpm-text-secondary)]',
        ].join(' ');

    const viewToggleClass = (active: boolean) =>
        [
            'inline-flex items-center rounded-[8px] transition-colors',
            active
                ? 'bg-white text-[var(--cpm-brand-navy)] shadow-[var(--cpm-shadow-hairline)]'
                : 'text-[var(--cpm-text-secondary)]',
        ].join(' ');

    return (
        <CpmPageShell gap={20} maxWidth={1420} variant="masterEditor">
            <div
                className="flex items-center text-[var(--cpm-text-secondary)]"
                style={{ gap: 8, fontSize: 12.5, fontWeight: 600 }}
            >
                <span>Admin</span>
                <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                    /
                </span>
                <span>Catalog</span>
                <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                    /
                </span>
                <Link
                    to="/central-product-master"
                    className="!text-[var(--cpm-text-secondary)] hover:!text-[var(--cpm-brand-navy)] !font-semibold"
                >
                    Central Product Master
                </Link>
                <span className="text-[var(--cpm-text-tertiary)]" style={{ fontWeight: 100 }}>
                    /
                </span>
                <span className="text-[var(--cpm-brand-navy)]" style={{ fontWeight: 800 }}>
                    Verification Queue
                </span>
            </div>

            <div className="flex items-end justify-between flex-wrap" style={{ gap: 32 }}>
                <div className="flex flex-col max-w-[720px]" style={{ gap: 6 }}>
                    <div
                        className="uppercase text-[var(--cpm-text-secondary)]"
                        style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                    >
                        Central Product Master
                    </div>
                    <h1 className="m-0 !text-[30px] !font-extrabold !tracking-[-0.025em] !leading-[1.05] !text-[var(--cpm-brand-navy)]">
                        Verification Queue
                    </h1>
                    <p className="m-0 !text-[14px] !font-medium !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                        Everything the pipeline could not decide on its own. Clear the large,
                        low-risk batches in bulk; open the rows that need a judgement call.
                    </p>
                </div>
                <div className="flex items-center" style={{ gap: 20 }}>
                    <div className="flex flex-col" style={{ gap: 4 }}>
                        <span
                            className="uppercase text-[var(--cpm-text-tertiary)]"
                            style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                        >
                            Open items
                        </span>
                        <span
                            className="cpm-fw-800 tabular text-[var(--cpm-brand-navy)]"
                            style={{
                                fontSize: 30,
                                fontWeight: 800,
                                letterSpacing: '-0.025em',
                                lineHeight: 1,
                            }}
                        >
                            {queue.counts ? formatCpmCount(queue.counts.openItems) : '—'}
                        </span>
                    </div>
                    <div className="w-px self-stretch bg-[var(--cpm-border-default)]" />
                    <div className="flex flex-col" style={{ gap: 4 }}>
                        <span
                            className="uppercase text-[var(--cpm-text-tertiary)]"
                            style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                        >
                            Need a person
                        </span>
                        <span
                            className="cpm-fw-800 tabular text-[var(--cpm-warning-text)]"
                            style={{
                                fontSize: 30,
                                fontWeight: 800,
                                letterSpacing: '-0.025em',
                                lineHeight: 1,
                            }}
                        >
                            {queue.counts ? formatCpmCount(queue.counts.needPerson) : '—'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="flex items-end gap-[4px] border-b border-[var(--cpm-border-default)]">
                {(
                    [
                        { key: 'pending' as const, label: 'Pending Promotion' },
                        { key: 'barcode' as const, label: 'Barcode Conflicts' },
                        { key: 'link' as const, label: 'Link Review' },
                    ] as const
                ).map((tab) => {
                    const active = activeTab === tab.key;
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => selectTab(tab.key)}
                            className={tabButtonClass(active)}
                            style={{
                                padding: '11px 16px 12px',
                                gap: 8,
                                fontSize: 13.5,
                                fontWeight: 800,
                                letterSpacing: '-0.01em',
                            }}
                        >
                            <span>{tab.label}</span>
                            {active ? (
                                <span
                                    className={tabBadgeClass(active)}
                                    style={{
                                        padding: '2px 9px',
                                        fontSize: 11,
                                        fontWeight: 800,
                                    }}
                                >
                                    {activeTabTotal == null ? '—' : formatCpmCount(activeTabTotal)}
                                </span>
                            ) : null}
                        </button>
                    );
                })}
            </div>

            {activeTab === 'pending' ? (
                <div
                    className="flex items-center rounded-[var(--cpm-radius-10)] bg-[var(--cpm-info-bg)] border border-[var(--cpm-info-border)]"
                    style={{ gap: 10, padding: '12px 16px' }}
                >
                    <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--cpm-info-icon)"
                        strokeWidth="2"
                        className="shrink-0"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
                    </svg>
                    <span
                        className="flex-1 text-[var(--cpm-info-text)]"
                        style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.45 }}
                    >
                        Most of these {formatCpmCount(activeTabTotal ?? 0)} records are expected to
                        clear in bulk after a spot-check.{' '}
                        <span style={{ fontWeight: 800 }}>Flagged possible duplicates</span> should
                        be looked at before you bulk-approve anything.
                    </span>
                    <button
                        type="button"
                        onClick={() =>
                            dispatch(
                                patchFilters({
                                    tab: 'pending',
                                    patch: { signals: ['DUP'] },
                                })
                            )
                        }
                        className="shrink-0 rounded-full bg-white border border-[var(--cpm-info-border)] text-[var(--cpm-info-text)]"
                        style={{
                            padding: '5px 12px',
                            fontSize: 11.5,
                            fontWeight: 800,
                        }}
                    >
                        Show flagged only
                    </button>
                </div>
            ) : null}

            <div
                className="relative bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] rounded-[16px] shadow-[var(--cpm-shadow-card)] flex flex-col"
                style={{ padding: '14px 16px', gap: 14 }}
            >
                <div className="flex items-end flex-wrap" style={{ gap: 12 }}>
                    {activeTab === 'link' ? (
                        <CpmFilterDropdown
                            label="Confidence band"
                            options={CPM_BAND_OPTIONS}
                            selected={filters.band}
                            emptyLabel="All bands"
                            onChange={(band) =>
                                dispatch(patchFilters({ tab: activeTab, patch: { band } }))
                            }
                        />
                    ) : null}
                    {activeTab === 'pending' ? (
                        <CpmFilterDropdown
                            label="Review signal"
                            options={CPM_SIGNAL_OPTIONS}
                            selected={filters.signals}
                            emptyLabel="All signals"
                            onChange={(signals) =>
                                dispatch(patchFilters({ tab: activeTab, patch: { signals } }))
                            }
                        />
                    ) : null}
                    {activeTab !== 'barcode' ? (
                        <CpmFilterDropdown
                            label="Matching method"
                            options={CPM_METHOD_OPTIONS}
                            selected={filters.method}
                            emptyLabel="All methods"
                            onChange={(method) =>
                                dispatch(patchFilters({ tab: activeTab, patch: { method } }))
                            }
                        />
                    ) : null}
                    {activeTab === 'barcode' ? (
                        <CpmFilterDropdown
                            label="Signal"
                            options={[
                                {
                                    value: 'conflict',
                                    label: 'Conflicted — two or more products claiming it',
                                },
                                {
                                    value: 'disputed',
                                    label: 'Disputed — stores stopped using a trusted code',
                                },
                            ]}
                            selected={
                                filters.disputed === true
                                    ? ['disputed']
                                    : filters.disputed === false
                                      ? ['conflict']
                                      : []
                            }
                            multi={false}
                            emptyLabel="All signals"
                            onChange={(vals) =>
                                dispatch(
                                    patchFilters({
                                        tab: activeTab,
                                        patch: {
                                            disputed:
                                                vals[0] === 'disputed'
                                                    ? true
                                                    : vals[0] === 'conflict'
                                                      ? false
                                                      : null,
                                        },
                                    })
                                )
                            }
                        />
                    ) : null}
                    {activeTab !== 'barcode' ? (
                        <CpmFilterDropdown
                            label="Record type"
                            options={CPM_RECORD_TYPE_OPTIONS}
                            selected={filters.recordType}
                            emptyLabel="All types"
                            onChange={(recordType) =>
                                dispatch(patchFilters({ tab: activeTab, patch: { recordType } }))
                            }
                        />
                    ) : null}

                    <CpmDateRangeFilter
                        dateFrom={filters.dateFrom}
                        dateTo={filters.dateTo}
                        onChange={({ dateFrom, dateTo }) =>
                            dispatch(patchFilters({ tab: activeTab, patch: { dateFrom, dateTo } }))
                        }
                    />
                </div>

                <div className="flex items-end flex-wrap" style={{ gap: 12 }}>
                    <CpmStoreBrandFilter
                        valueId={filters.storeBrand}
                        valueLabel={filters.storeBrandLabel}
                        queueType={storeBrandQueueType}
                        filterContext={storeBrandFilterContext}
                        onChange={({ id, label }) =>
                            dispatch(
                                patchFilters({
                                    tab: activeTab,
                                    patch: { storeBrand: id, storeBrandLabel: label },
                                })
                            )
                        }
                        className="!flex-1 !w-full !max-w-none"
                    />

                    <CpmFilterDropdown
                        label="Sort by"
                        options={sortOptions.map((o) => ({ value: o.value, label: o.label }))}
                        selected={currentSort ? [currentSort] : ['']}
                        multi={false}
                        includeEmptyOption={false}
                        emptyLabel={sortShort}
                        triggerLabel={sortShort}
                        highlightSelected={!!currentSort}
                        leadingIcon={<IconSort />}
                        onChange={(vals) => {
                            const token = vals[0] ?? '';
                            const next = applySortToken(token, activeTab);
                            dispatch(patchFilters({ tab: activeTab, patch: next }));
                        }}
                        className="!flex-none max-w-[220px]"
                    />

                    {activeTab === 'pending' || activeTab === 'link' ? (
                        <div className="ml-auto">
                            <CpmButton
                                variant="navy"
                                size="sm"
                                onClick={() =>
                                    setBulkModal(
                                        activeTab === 'link'
                                            ? { kind: 'resolve', path: 'filtered' }
                                            : { kind: 'approve', path: 'filtered' }
                                    )
                                }
                                leadingIcon={<IconCheck size={14} />}
                                style={{
                                    height: 38,
                                    fontSize: 12.5,
                                    fontWeight: 800,
                                    paddingLeft: 15,
                                    paddingRight: 15,
                                    letterSpacing: '-0.01em',
                                }}
                            >
                                {activeTab === 'link' ? 'Accept AI for' : 'Approve'} all{' '}
                                {formatCpmCount(matchingTotal)} matching
                            </CpmButton>
                        </div>
                    ) : null}
                </div>

                {activeFilterChips.length > 0 ? (
                    <div className="flex items-center gap-[10px] flex-wrap">
                        <span className="inline-flex items-center gap-[6px] text-[10.5px] font-extrabold tracking-[0.07em] uppercase text-[var(--cpm-text-tertiary)] whitespace-nowrap">
                            <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                            >
                                <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
                            </svg>
                            Active filters
                            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-[5px] rounded-full bg-[var(--cpm-brand-purple)] text-white text-[11px] font-extrabold tabular">
                                {activeFilterChips.length}
                            </span>
                        </span>
                        {activeFilterChips.map((chip) => (
                            <span
                                key={`${chip.key}-${chip.value}`}
                                className="inline-flex items-center gap-[8px] pl-[11px] pr-[7px] py-[5px] rounded-full bg-[var(--cpm-purple-tint-3)] border-[1.5px] border-[var(--cpm-purple-accent-border)]"
                            >
                                <span className="text-[9.5px] font-extrabold tracking-[0.05em] uppercase text-[var(--cpm-brand-purple)]">
                                    {chip.key}
                                </span>
                                <span className="text-[12.5px] font-extrabold tracking-[-0.01em] text-[var(--cpm-brand-navy)] whitespace-nowrap">
                                    {chip.value}
                                </span>
                                <button
                                    type="button"
                                    title="Remove this filter"
                                    onClick={chip.clear}
                                    className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[var(--cpm-brand-purple)] text-white"
                                >
                                    <svg
                                        width="10"
                                        height="10"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                    >
                                        <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </span>
                        ))}
                        <button
                            type="button"
                            onClick={() => dispatch(clearFilters(activeTab))}
                            className="text-[12px] font-extrabold text-[var(--cpm-text-secondary)] border-b-[1.5px] border-[var(--cpm-border-strong)] hover:text-[var(--cpm-brand-navy)] hover:border-[var(--cpm-brand-navy)]"
                        >
                            Clear all
                        </button>
                    </div>
                ) : null}
            </div>

            <CpmSelectionBar
                count={queue.selectedIds.length}
                warning={
                    dupSelectedCount > 0
                        ? `${dupSelectedCount} selected ${dupSelectedCount === 1 ? 'row is' : 'rows are'} flagged as possible duplicates`
                        : undefined
                }
            >
                {canReject ? (
                    <CpmButton
                        variant="selectionGhost"
                        size="selection"
                        className="px-[16px] text-[13px]"
                        leadingIcon={<IconX />}
                        onClick={() => setRejectTarget(selectedPending[0])}
                    >
                        Reject
                    </CpmButton>
                ) : null}
                {canMerge ? (
                    <CpmButton
                        variant="selectionGhost"
                        size="selection"
                        className="px-[16px] text-[13px]"
                        leadingIcon={<IconMerge />}
                        onClick={() =>
                            setMergeState({
                                level: selectedPending[0].level,
                                idA: selectedPending[0]._id,
                                idB: selectedPending[1]._id,
                            })
                        }
                    >
                        Merge
                    </CpmButton>
                ) : null}
                {canBulkApprove ? (
                    <CpmButton
                        variant="selection"
                        size="selection"
                        leadingIcon={<IconCheck size={15} />}
                        onClick={() => setBulkModal({ kind: 'approve', path: 'picked' })}
                    >
                        Bulk approve
                    </CpmButton>
                ) : null}
                {canBulkResolve ? (
                    <CpmButton
                        variant="selection"
                        size="selection"
                        leadingIcon={<IconCheck size={15} />}
                        onClick={() => setBulkModal({ kind: 'resolve', path: 'picked' })}
                    >
                        Accept AI for selected
                    </CpmButton>
                ) : null}
            </CpmSelectionBar>

            <div className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] rounded-[16px] shadow-[var(--cpm-shadow-card)] overflow-hidden">
                {activeTab === 'pending' ? (
                    <div className="flex items-center justify-between gap-[12px] flex-nowrap px-[18px] py-[11px] border-b border-[var(--cpm-border-default)] shrink-0">
                        <span
                            className="flex-1 min-w-0 text-[var(--cpm-text-secondary)]"
                            style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.45 }}
                        >
                            {queue.pendingView === 'flat'
                                ? 'Flat list in the order records were flagged. Use Sort by, or switch to Grouped, to spot look-alikes.'
                                : 'Grouped by parent chain so you can approve a brand → product → variant stack together.'}
                        </span>
                        <div className="shrink-0 inline-flex items-center gap-[3px] p-[3px] rounded-[10px] bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-default)]">
                            <button
                                type="button"
                                onClick={() => dispatch(setPendingView('grouped'))}
                                className={viewToggleClass(queue.pendingView === 'grouped')}
                                style={{
                                    height: 32,
                                    paddingLeft: 12,
                                    paddingRight: 12,
                                    gap: 6,
                                    fontSize: 12.5,
                                    fontWeight: 800,
                                }}
                            >
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M3 3v16a2 2 0 0 0 2 2h16" />
                                    <path d="M7 11h4M11 7h6M11 15h8" strokeLinecap="round" />
                                </svg>
                                Grouped by chain
                            </button>
                            <button
                                type="button"
                                onClick={() => dispatch(setPendingView('flat'))}
                                className={viewToggleClass(queue.pendingView === 'flat')}
                                style={{
                                    height: 32,
                                    paddingLeft: 12,
                                    paddingRight: 12,
                                    gap: 6,
                                    fontSize: 12.5,
                                    fontWeight: 800,
                                }}
                            >
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <line x1="8" y1="6" x2="21" y2="6" />
                                    <line x1="8" y1="12" x2="21" y2="12" />
                                    <line x1="8" y1="18" x2="21" y2="18" />
                                    <line x1="3" y1="6" x2="3.01" y2="6" />
                                    <line x1="3" y1="12" x2="3.01" y2="12" />
                                    <line x1="3" y1="18" x2="3.01" y2="18" />
                                </svg>
                                Flat list
                            </button>
                        </div>
                    </div>
                ) : null}

                {/* Same internal scroll + fixed max-height as Master Editor tables. */}
                <div
                    className="overflow-auto transition-opacity duration-150"
                    style={{
                        maxHeight: 'calc(100vh - 132px)',
                        opacity: tableBusyPage ? 0.45 : 1,
                        pointerEvents: tableBusyPage ? 'none' : undefined,
                    }}
                >
                    {tableReplaceLoading ? (
                        <CpmQueueListLoading
                            label={
                                activeTab === 'pending'
                                    ? 'Loading pending promotions'
                                    : activeTab === 'link'
                                      ? 'Loading link review'
                                      : activeTab === 'barcode'
                                        ? 'Loading barcode conflicts'
                                        : 'Loading the queue'
                            }
                        />
                    ) : bucket.loading === 'error' && bucket.items.length === 0 ? (
                        <CpmInlineErrorBanner
                            message={bucket.error || 'Failed to load queue'}
                            onRetry={refresh}
                        />
                    ) : bucket.items.length === 0 ? (
                        <CpmEmptyState
                            title="Nothing in this queue"
                            description="No rows match the current filters."
                        />
                    ) : (
                        <>
                            {activeTab === 'pending' && queue.pendingView === 'grouped' ? (
                                <PendingGroupedView
                                    groupChains={groupChains}
                                    standalone={standalone}
                                    promotedIds={promotedIds}
                                    mutating={mutating}
                                    onOpen={(item) => openReview({ mode: 'pending', item })}
                                    onApproveChain={handleApproveChain}
                                />
                            ) : null}

                            {activeTab === 'pending' && queue.pendingView === 'flat' ? (
                                <div>
                                    <div
                                        className="cpm-table-head grid grid-cols-[38px_minmax(0,2fr)_minmax(0,1fr)_250px_26px] gap-[14px] items-center px-[18px] py-[12px] bg-[var(--cpm-surface-subtle)] border-b border-[var(--cpm-border-default)] sticky top-0 z-[14] shadow-[inset_0_-1px_0_var(--cpm-border-default)]"
                                        style={{
                                            fontSize: 10,
                                            fontWeight: 800,
                                            letterSpacing: '0.08em',
                                            color: 'var(--cpm-text-primary)',
                                            fontFamily:
                                                "var(--cpm-font-sans), 'Inter', system-ui, sans-serif",
                                        }}
                                    >
                                        <CpmCheckbox
                                            checked={
                                                pendingItems.length > 0 &&
                                                pendingItems.every((i) =>
                                                    queue.selectedIds.includes(i._id)
                                                )
                                            }
                                            onChange={(e) =>
                                                dispatch(
                                                    setPageSelection({
                                                        items: pendingItems,
                                                        checked: e.target.checked,
                                                    })
                                                )
                                            }
                                        />
                                        <span>Record awaiting promotion</span>
                                        <span>How it was created</span>
                                        <span>Review signal</span>
                                        <span />
                                    </div>
                                    {pendingItems.map((row) => {
                                        const selected = queue.selectedIds.includes(row._id);
                                        const blocked = !row.parentIsActive && !!row.parent;
                                        const dup = !!row.possibleDuplicateOf;
                                        const pack = packLabel(row);
                                        return (
                                            <div
                                                key={row._id}
                                                className={[
                                                    'grid grid-cols-[38px_minmax(0,2fr)_minmax(0,1fr)_250px_26px] gap-[14px] items-center px-[18px] py-[15px] border-t border-[var(--cpm-border-default)] cursor-pointer hover:bg-[var(--cpm-surface-subtle)]',
                                                    selected
                                                        ? 'bg-[var(--cpm-purple-tint-3)]'
                                                        : dup
                                                          ? 'bg-[var(--cpm-warning-bg)]'
                                                          : '',
                                                ].join(' ')}
                                                onClick={() => openPendingRow(row)}
                                            >
                                                <div onClick={(e) => e.stopPropagation()}>
                                                    <CpmCheckbox
                                                        checked={selected}
                                                        onChange={() =>
                                                            dispatch(toggleSelectedItem(row))
                                                        }
                                                    />
                                                </div>
                                                <div className="min-w-0 flex flex-col gap-[5px]">
                                                    <div className="text-[13.5px] font-extrabold tracking-[-0.01em] truncate text-[var(--cpm-text-primary)]">
                                                        {row.name}
                                                    </div>
                                                    <div className="flex items-center gap-[8px] flex-wrap min-w-0 overflow-hidden">
                                                        <CpmTypeChip recordType={row.recordType} />
                                                        {pack ? (
                                                            <span className="text-[11.5px] font-bold text-[var(--cpm-text-secondary)] tabular truncate">
                                                                {pack}
                                                            </span>
                                                        ) : null}
                                                        <span className="text-[11px] font-bold tracking-[0.04em] text-[var(--cpm-text-tertiary)] font-[family-name:var(--cpm-font-mono)]">
                                                            PENDING
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col gap-[4px]">
                                                    <span className="text-[13px] font-bold text-[var(--cpm-text-primary)]">
                                                        {formatCreatedVia(
                                                            row.provenance?.createdBy
                                                        )}
                                                    </span>
                                                    <span className="text-[12px] font-semibold text-[var(--cpm-text-secondary)]">
                                                        {formatFlaggedDate(row.createdAt)}
                                                    </span>
                                                </div>
                                                <div className="flex flex-col gap-[4px] min-w-0">
                                                    {blocked ? (
                                                        <>
                                                            <span className="inline-flex items-center gap-[5px] self-start px-[9px] py-[3px] rounded-full bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[10.5px] font-extrabold tracking-[0.04em] text-[var(--cpm-shortfall-text)]">
                                                                <svg
                                                                    width="11"
                                                                    height="11"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2.4"
                                                                >
                                                                    <rect
                                                                        width="18"
                                                                        height="11"
                                                                        x="3"
                                                                        y="11"
                                                                        rx="2"
                                                                    />
                                                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                                                </svg>
                                                                Blocked
                                                            </span>
                                                            <span className="text-[11.5px] font-semibold leading-[1.4] text-[var(--cpm-shortfall-text-med)]">
                                                                Blocked — parent{' '}
                                                                {row.parent?.level?.toLowerCase() ??
                                                                    'record'}{' '}
                                                                &lsquo;{row.parent?.name}&rsquo;
                                                                isn&apos;t approved yet
                                                            </span>
                                                            {row.parent?.id ? (
                                                                <button
                                                                    type="button"
                                                                    className="self-start text-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-navy)]"
                                                                    style={{
                                                                        fontSize: 11.5,
                                                                        fontWeight: 800,
                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        openPendingById(
                                                                            row.parent!.id
                                                                        );
                                                                    }}
                                                                >
                                                                    Go to parent{' '}
                                                                    {row.parent.level?.toLowerCase() ??
                                                                        'record'}{' '}
                                                                    →
                                                                </button>
                                                            ) : null}
                                                        </>
                                                    ) : dup ? (
                                                        <>
                                                            <span className="inline-flex self-start px-[9px] py-[3px] rounded-full bg-white border border-[var(--cpm-warning-border)] text-[10.5px] font-extrabold tracking-[0.04em] text-[var(--cpm-warning-text)]">
                                                                Possible duplicate
                                                            </span>
                                                            <span className="text-[11.5px] font-semibold text-[var(--cpm-warning-text-med)] truncate">
                                                                Possibly a duplicate of &lsquo;
                                                                {duplicateTargetName(row)}&rsquo;
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span className="inline-flex self-start px-[9px] py-[3px] rounded-full bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-default)] text-[10.5px] font-bold text-[var(--cpm-text-secondary)]">
                                                            No flags
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="inline-flex items-center justify-center">
                                                    {blocked ? (
                                                        <svg
                                                            width="15"
                                                            height="15"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="var(--cpm-shortfall-text)"
                                                            strokeWidth="2"
                                                        >
                                                            <rect
                                                                width="18"
                                                                height="11"
                                                                x="3"
                                                                y="11"
                                                                rx="2"
                                                            />
                                                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                                        </svg>
                                                    ) : (
                                                        <svg
                                                            width="16"
                                                            height="16"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="var(--cpm-text-tertiary)"
                                                            strokeWidth="2"
                                                        >
                                                            <path
                                                                d="m9 18 6-6-6-6"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    )}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : null}

                            {activeTab === 'link' ? (
                                <LinkReviewTable
                                    items={linkItems}
                                    selectedIds={queue.selectedIds}
                                    mutating={mutating}
                                    onToggle={(row) => dispatch(toggleSelectedItem(row))}
                                    onToggleAll={(checked) =>
                                        dispatch(setPageSelection({ items: linkItems, checked }))
                                    }
                                    onOpen={(item) => openReview({ mode: 'link', item })}
                                    onAccept={handleAcceptLink}
                                />
                            ) : null}

                            {activeTab === 'barcode' ? (
                                <BarcodeConflictTable
                                    items={barcodeItems}
                                    selectedIds={queue.selectedIds}
                                    onToggle={(row) => dispatch(toggleSelectedItem(row))}
                                    onToggleAll={(checked) =>
                                        dispatch(setPageSelection({ items: barcodeItems, checked }))
                                    }
                                    onOpen={(item) => openReview({ mode: 'barcode', item })}
                                />
                            ) : null}
                        </>
                    )}
                </div>

                {bucket.pagination && !tableReplaceLoading ? (
                    <div
                        className="flex items-center flex-wrap gap-[16px] border-t border-[var(--cpm-border-default)] bg-[var(--cpm-surface-subtle)] rounded-b-[16px]"
                        style={{ padding: '12px 18px' }}
                    >
                        <CpmPaginationLabel
                            page={bucket.pagination.page}
                            limit={bucket.pagination.limit}
                            total={bucket.pagination.total}
                            noun={paginationNoun}
                        />
                        {tableBusyPage && queue.pageBusyLabel ? (
                            <span className="inline-flex items-center" style={{ gap: 8 }}>
                                <span
                                    className="cpm-spin"
                                    style={{ width: 13, height: 13, borderWidth: 2 }}
                                />
                                <span
                                    className="uppercase text-[var(--cpm-text-tertiary)]"
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 800,
                                        letterSpacing: '0.06em',
                                    }}
                                >
                                    {queue.pageBusyLabel}
                                </span>
                            </span>
                        ) : null}
                        <div className="flex items-center gap-[8px]">
                            <span
                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                style={{
                                    fontSize: 10.5,
                                    fontWeight: 800,
                                    letterSpacing: '0.06em',
                                }}
                            >
                                {activeTab === 'pending' && queue.pendingView === 'grouped'
                                    ? 'Chains'
                                    : 'Rows'}
                            </span>
                            <CpmFilterDropdown
                                label=""
                                options={[
                                    { value: '25', label: '25' },
                                    { value: '50', label: '50' },
                                    { value: '100', label: '100' },
                                ]}
                                selected={[String(filters.limit)]}
                                multi={false}
                                includeEmptyOption={false}
                                emptyLabel={String(filters.limit)}
                                triggerLabel={String(filters.limit)}
                                highlightSelected={false}
                                onChange={(vals) => {
                                    const n = parseInt(vals[0] || '25', 10);
                                    dispatch(
                                        patchFilters({
                                            tab: activeTab,
                                            patch: { limit: n, page: 1 },
                                        })
                                    );
                                }}
                                className="!flex-none !min-w-[72px] !max-w-[88px]"
                                minWidth={72}
                                openUp
                            />
                        </div>
                        <div className="ml-auto [&_>div]:border-t-0 [&_>div]:bg-transparent [&_>div]:rounded-none [&_>div]:p-0">
                            <CpmPagination
                                page={bucket.pagination.page}
                                limit={bucket.pagination.limit}
                                total={bucket.pagination.total}
                                onPageChange={(page) => dispatch(setPage({ tab: activeTab, page }))}
                                hideLabel
                            />
                        </div>
                    </div>
                ) : null}
            </div>

            {rejectTarget ? (
                <RejectModal
                    open
                    level={rejectTarget.level}
                    masterId={rejectTarget._id}
                    name={rejectTarget.name}
                    recordType={rejectTarget.recordType}
                    onClose={() => setRejectTarget(null)}
                    onRejected={refresh}
                />
            ) : null}

            <QuickReviewModal
                open={!!quickTarget}
                item={quickTarget}
                onClose={() => setQuickTarget(null)}
                onOpenFull={(item) => openReview({ mode: 'pending', item })}
                onApproved={() => {
                    dispatch(fetchQueueCounts());
                }}
            />

            {bulkModal ? (
                <BulkActionModal
                    open
                    kind={bulkModal.kind}
                    path={bulkModal.path}
                    tab={activeTab}
                    total={
                        bulkModal.path === 'filtered'
                            ? matchingTotal
                            : bulkModal.kind === 'resolve'
                              ? selectedLinks.length
                              : selectedPending.length
                    }
                    rows={
                        bulkModal.kind === 'resolve'
                            ? bulkModal.path === 'filtered'
                                ? linkItems
                                : selectedLinks
                            : bulkModal.path === 'filtered'
                              ? pendingItems
                              : selectedPending
                    }
                    filters={{
                        band: filters.band,
                        signals: filters.signals,
                        method: filters.method,
                        recordType: filters.recordType,
                        storeBrandLabel: filters.storeBrandLabel,
                        dateFrom: filters.dateFrom,
                        dateTo: filters.dateTo,
                    }}
                    remainingAfter={Math.max(
                        0,
                        (activeTabTotal ?? 0) -
                            (bulkModal.path === 'filtered'
                                ? matchingTotal
                                : bulkModal.kind === 'resolve'
                                  ? selectedLinks.length
                                  : selectedPending.length)
                    )}
                    onClose={() => setBulkModal(null)}
                    onConfirm={runBulkConfirm}
                    onJumpBlocked={() =>
                        dispatch(
                            patchFilters({
                                tab: 'pending',
                                patch: { signals: [] },
                            })
                        )
                    }
                />
            ) : null}

            <ReviewDetail
                open={!!(reviewPending || reviewLink || reviewBarcode)}
                mode={reviewTarget?.mode ?? 'pending'}
                pendingItem={reviewPending}
                linkItem={reviewLink}
                barcodeItem={reviewBarcode}
                onClose={closeReview}
                onChanged={refresh}
                onOpenMerge={(level, aId, bId) => {
                    closeReview();
                    setMergeState({ level, idA: aId, idB: bId });
                }}
                onOpenReject={(item) => {
                    closeReview();
                    setRejectTarget(item);
                }}
                onOpenNext={(id) => {
                    openPendingById(id);
                }}
            />

            {mergeState ? (
                <MergeDecision
                    open
                    level={mergeState.level}
                    idA={mergeState.idA}
                    idB={mergeState.idB}
                    backLabel="Verification Queue"
                    onClose={() => setMergeState(null)}
                    onMerged={refresh}
                    onOpenSurvivor={(level, id) => {
                        setMergeState(null);
                        navigate(masterEditorHrefForSurvivor(level, id));
                    }}
                />
            ) : null}
        </CpmPageShell>
    );
}
